import axios from 'axios';
// @ts-ignore
import { ParseCoordinates } from '../Utils/CountryUtility';
const backendUrl = process.env.REACT_APP_BACKEND_URL;

export interface FoodPost {
  id: number;
  iso: string;
  region: string;
  dish: string;
  restaurant: string;
  food_category: string;
  description: string;
  images: string[]; // URLs from img.get_image_url()
  attributions: string[]; // URLs from img.get_attribution()
  coordinates: string;
  created_at: string; // ISO date string
  updated_at: string; // ISO date string
}

type TFetchFood = (
  iso: string,
  options: {
    size?: number; // page size
    cursor?: string; // cursor for pagination
    cursorToken?: string; // server-issued token bound to cursor context
    category?: string; // filter
    region?: string; // filter
    selectedItemId?: number; // item to scroll to (only used on initial load)
  },
  setOldCursor: (nextCursor: string) => void,
  setNewCursor: (previousCursor: string) => void,
  setCurrentCursor: (cursor: string) => void,
  setCursorToken: (cursorToken: string) => void,
  setCategory: (category: string) => void,
  setRegion: (region: string) => void,
  setCurrentPage: (currentPage: number) => void,
  setFoodData: (data: FoodPost[]) => void,
  setMapCenter: (coords: [number, number]) => void,
  setShowMap: (show: boolean) => void,
  setActivePost: (index: number) => void,
  setActiveMarker: (index: number) => void,
  itemRefs: React.RefObject<(HTMLDivElement | null)[]>,
) => void;

export function FetchNumberOfFoodPosts(iso?: string): Promise<number> {
  // Determine the URL based on whether 'iso' is provided
  const url = iso ? `${backendUrl}/worlddata/food/get-food-counter/${iso}` : `${backendUrl}/worlddata/food/get-food-total-counter/`;

  // Make the API request and return the number of posts
  return axios
    .get(url)
    .then((response) => {
      return response.data.food_counter || response.data.food_counter_total; // Return the number of posts
    })
    .catch((error) => {
      console.error('Error fetching food posts:', error);
      return 0; // Return 0 if there's an error
    });
}

export async function FetchRandomFood(size: number, cache_key: string, iso?: string, food_category?: string): Promise<any[]> {
  try {
    let url = `${backendUrl}/worlddata/food/get-random-food-data/${size}/?cache_key=${cache_key}`;
    if (iso) url += `&iso=${iso}`;
    if (food_category) url += `&food_category=${food_category}`;

    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching food data:', error);
    throw error;
  }
}

export async function FetchRandomFoodBulkCategory(size: number, cache_key: string, iso?: string): Promise<Record<string, any[]>> {
  try {
    let url = `${backendUrl}/worlddata/food/get-random-food-data-bulk-category/${size}/?cache_key=${cache_key}`;
    if (iso) url += `&iso=${iso}`;

    const response = await axios.get(url);
    return response.data; // shape: { "MainDish": [...], "Dessert": [...], ... }
  } catch (error) {
    console.error('Error fetching bulk food data:', error);
    throw error;
  }
}

export function FetchNumberOfFoodPostsPerContinent(): Promise<any> {
  const url = `${backendUrl}/worlddata/food/get-food-continent-counter/`;

  return axios.get(url)
    .then((response) => response.data.food_counter_continent || {})
    .catch((error) => {
      console.error('Error fetching food posts:', error);
      return {};
    });
}

export const FetchFood: TFetchFood = async (
  iso,
  { size = 9, cursor = '', cursorToken = '', category = '', region = '', selectedItemId = null },
  setOldCursor,
  setNewCursor,
  setCurrentCursor,
  setCursorToken,
  setCategory,
  setRegion,
  setCurrentPage,
  setFoodData,
  setMapCenter,
  setShowMap,
  setActivePost,
  setActiveMarker,
  itemRefs,
) => {
  const effectiveSize = Number.isFinite(size) && size > 0 ? size : 9;
  const cursorParam = cursor ? `&cursor=${cursor}` : '';
  const cursorTokenParam = cursor && cursorToken ? `&cursor_token=${encodeURIComponent(cursorToken)}` : '';
  const categoryParam = category ? `&food_category=${category}` : '';
  const regionParam = region ? `&region=${encodeURIComponent(region)}` : '';
  const isoParam = iso ? `iso=${encodeURIComponent(iso)}` : '';
  const extraParams = `${cursorParam}${cursorTokenParam}${categoryParam}${regionParam}`;
  const queryTail = `${isoParam}${extraParams}`;
  const url = `${backendUrl}/worlddata/food/get-food-data/${effectiveSize}/?${queryTail}`;

  try {
    const response = await axios.get(url);

    const items = response.data.items;

    setOldCursor(response.data.old_cursor);
    setNewCursor(response.data.new_cursor);
    setCurrentCursor(cursor);
    setCursorToken(response.data.cursor_token || '');
    setCategory(category);
    setRegion(region);
    setCurrentPage(response.data.page);
    setFoodData(items);

    if (items.length > 0) {
      const [firstPost] = items;
      const [lat, lon] = ParseCoordinates(firstPost.coordinates);

      if (!isNaN(lat) && !isNaN(lon)) {
        setMapCenter([lat, lon]);
        setShowMap(true);
      } else {
        setShowMap(false);
      }
    }

    // If a specific item was passed, scroll to it
    if (selectedItemId) {
      const index = items.findIndex((item: FoodPost) => item.id === Number(selectedItemId));
      if (index !== -1) {
        setActivePost(index);
        setActiveMarker(index);
        setTimeout(() => {
          itemRefs.current?.[index]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 100);
      }
    }
  } catch (error) {
    console.error('Error fetching sightseeing data:', error);
  }
};
